import { snapshotDefinition } from "@healthos/shared";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useState } from "react";
import { cn } from "../../lib/cn";

// A term the member did not choose to learn. The term itself is the control, so
// the definition arrives on hover, on focus, and on a tap, and there is one
// thing to reach rather than two.
//
// Radix, the same primitive the shell's mode badge uses, so the content is
// portalled out of the card and a definition on a term near an edge is placed
// against the viewport instead of being clipped by the card it sits in. Open
// state is held here because Radix's tooltip is a pointer and focus control
// only: it treats a tap as a click and closes, which on a phone means the
// definition flashes and leaves. Holding the state lets a tap toggle it.
export function Term({ label, definition }: { label: string; definition: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip.Provider delayDuration={150} skipDelayDuration={0}>
      <Tooltip.Root open={open} onOpenChange={setOpen}>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            className="snap-term-btn"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="snap-term-t">{label}</span>
            <span className="snap-term-q" aria-hidden="true">
              ?
            </span>
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="bottom"
            align="start"
            sideOffset={6}
            collisionPadding={12}
            className={cn(
              "z-50 max-w-72 rounded-lg bg-navy px-3 py-2 text-xs leading-relaxed text-white shadow-lg",
              "motion-safe:data-[state=delayed-open]:animate-in motion-safe:data-[state=delayed-open]:fade-in-0",
            )}
          >
            {definition}
            <Tooltip.Arrow className="fill-navy" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

/** The label, with its definition attached when the glossary has one. */
export function Termed({ label, definition }: { label: string; definition: string | null }) {
  return definition ? <Term label={label} definition={definition} /> : label;
}

/** The parts of a named reading, structural so ui needs no client-data dep. */
export interface ReadingParts {
  label: string;
  termKey: string | null;
  rest: string;
}

/** A reading named for a level card: */
export function TrendToMove({
  parts,
  plain = false,
}: {
  parts: ReadingParts | null;
  /** Inside another control, where a nested button is invalid HTML. */
  plain?: boolean;
}) {
  if (!parts) return null;
  return (
    <>
      <Termed label={parts.label} definition={plain ? null : snapshotDefinition(parts.termKey)} />
      {parts.rest}
    </>
  );
}
